import React, { useEffect, useState, useRef } from "react";
import { Outlet, useNavigate, useLocation, NavLink } from "react-router-dom";
import {
  Menu,
  X,
  Home,
  Users,
  UserPlus,
  CreditCard,
  Receipt,
  BarChart3,
  Settings,
  LogOut,
  Calculator,
  Sun,
  Moon,
  Monitor,
  MapPin,
  Building2,
  UserCheck,
  Wallet,
  Shield,
  Tag,
  Plus,
  ChevronDown,
  ChevronRight,
  Eye,
  TrendingUp,
} from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import { useFeatureAccessStore } from "../../stores/featureAccessStore";
import { NetworkStatus } from "../NetworkStatus";
import PraivoxBranding from "../PraivoxBranding";
import { APP_SHORT_NAME, APP_TAGLINE } from "../../constants/branding";

type Theme = "light" | "dark" | "system";

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Reserved for future printer validation
  // @ts-ignore - intentionally unused for now
  const [_hasPrinters, setHasPrinters] = useState<boolean>(true);
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcPosition, setCalcPosition] = useState({ x: 100, y: 100 });
  const [calcSize, setCalcSize] = useState({ width: 300, height: 360 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0 });
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    donations: false,
    expenses: false,
    income: false,
    organization: false,
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timeOffset, setTimeOffset] = useState({ x: 0, y: 0 });
  const calcRef = useRef<HTMLDivElement>(null);
  const { user, logout, updateTheme, getCurrentTheme } = useAuthStore();
  const { currentFeatures, fetchCurrentUserFeatures } = useFeatureAccessStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Get current theme from auth store
  const theme = getCurrentTheme();

  // Structured navigation with submenus
  const navigationStructure = {
    topLevel: [
      {
        name: "Dashboard",
        href: "/",
        icon: Home,
        roles: ["SuperAdmin", "Admin", "Cashier"] as const,
        feature: undefined,
      },
    ],
    submenus: {
      donations: {
        name: "Donations",
        icon: CreditCard,
        expandedIcon: CreditCard,
        collapsedIcon: CreditCard,
        items: [
          {
            name: "Add Donation",
            href: "/add-donation",
            icon: Plus,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "addDonation" as const,
          },
          {
            name: "Manage Donations",
            href: "/donations",
            icon: CreditCard,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageDonations" as const,
          },
          {
            name: "Add Donors",
            href: "/add-donors",
            icon: UserPlus,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageDonors" as const,
          },
          {
            name: "Manage Donors",
            href: "/donors",
            icon: Users,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageDonors" as const,
          },
          {
            name: "Add Guest Donors",
            href: "/add-guest-donors",
            icon: UserPlus,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageDonors" as const,
          },
          {
            name: "Manage Guest Donors",
            href: "/guest-donors",
            icon: Users,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageDonors" as const,
          },
          {
            name: "Donation Types",
            href: "/donation-types",
            icon: Tag,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageDonationTypes" as const,
          },
        ],
      },
      expenses: {
        name: "Expenses",
        icon: Receipt,
        expandedIcon: Receipt,
        collapsedIcon: Receipt,
        items: [
          {
            name: "Add Expense",
            href: "/add-expense",
            icon: Plus,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "addExpense" as const,
          },
          {
            name: "Manage Expenses",
            href: "/expenses",
            icon: Receipt,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageExpenses" as const,
          },
          {
            name: "Expense Types",
            href: "/expense-types",
            icon: Tag,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageExpenseTypes" as const,
          },
        ],
      },
      income: {
        name: "Income",
        icon: TrendingUp,
        expandedIcon: TrendingUp,
        collapsedIcon: TrendingUp,
        items: [
          {
            name: "Add Income",
            href: "/add-income",
            icon: Plus,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "addIncome" as const,
          },
          {
            name: "Manage Incomes",
            href: "/incomes",
            icon: TrendingUp,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageIncomes" as const,
          },
          {
            name: "Income Departments",
            href: "/income-departments",
            icon: Building2,
            roles: ["SuperAdmin", "Admin"] as const,
            feature: "manageIncomeDepartments" as const,
          },
          {
            name: "Income Types",
            href: "/income-types",
            icon: Tag,
            roles: ["SuperAdmin", "Admin"] as const,
            feature: "manageIncomeTypes" as const,
          },
        ],
      },
      organization: {
        name: "Organization",
        icon: Building2,
        expandedIcon: Building2,
        collapsedIcon: Building2,
        items: [
          {
            name: "Regions",
            href: "/view-regions",
            icon: MapPin,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "viewRegions" as const,
          },
          {
            name: "Offices",
            href: "/view-offices",
            icon: Building2,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "viewOffices" as const,
          },
          {
            name: "Controllers",
            href: "/view-controllers",
            icon: UserCheck,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "viewControllers" as const,
          },
          {
            name: "Manage Regions",
            href: "/regions",
            icon: MapPin,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageRegions" as const,
          },
          {
            name: "Manage Offices",
            href: "/offices",
            icon: Building2,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageOffices" as const,
          },
          {
            name: "Manage Controllers",
            href: "/controllers",
            icon: UserCheck,
            roles: ["SuperAdmin", "Admin", "Cashier"] as const,
            feature: "manageControllers" as const,
          },
        ],
      },
    },
    bottomLevel: [
      {
        name: "Accounts",
        href: "/accounts",
        icon: Wallet,
        roles: ["SuperAdmin", "Admin", "Cashier"] as const,
        feature: "viewAccounts" as const,
      },
      {
        name: "Reports",
        href: "/reports",
        icon: BarChart3,
        roles: ["SuperAdmin", "Admin", "Cashier"] as const,
        feature: "accessReports" as const,
      },
      {
        name: "Automated Backups",
        href: "/automation-backups",
        icon: Settings,
        roles: ["SuperAdmin", "Admin", "Cashier"] as const,
        feature: "manageBackupAutomation" as const,
      },
      {
        name: "Scheduled Reports",
        href: "/automation-reports",
        icon: BarChart3,
        roles: ["SuperAdmin", "Admin", "Cashier"] as const,
        feature: "manageScheduledReports" as const,
      },
      {
        name: "Users",
        href: "/users",
        icon: Shield,
        roles: ["SuperAdmin", "Admin", "Cashier"] as const,
        feature: "manageUsers" as const,
      },
      {
        name: "Audit Logs",
        href: "/audit-logs",
        icon: Eye,
        roles: ["SuperAdmin"] as const,
        feature: undefined,
      },
      {
        name: "Settings",
        href: "/settings",
        icon: Settings,
        roles: ["SuperAdmin", "Admin", "Cashier"] as const,
        feature: undefined,
      },
    ],
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Smart time hover handler - moves across entire screen and avoids cursor!
  const handleTimeHover = (event: React.MouseEvent) => {
    // Get screen dimensions
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // Get current cursor position
    const cursorX = event.clientX;
    const cursorY = event.clientY;

    // Calculate safe zones (far from cursor)
    let targetX, targetY;

    // Try to find a position far from cursor
    for (let attempts = 0; attempts < 10; attempts++) {
      targetX = Math.random() * screenWidth;
      targetY = Math.random() * screenHeight;

      // Check if position is far enough from cursor (at least 200px away)
      const distanceFromCursor = Math.sqrt(
        Math.pow(targetX - cursorX, 2) + Math.pow(targetY - cursorY, 2),
      );

      if (distanceFromCursor > 200) {
        break; // Found a safe position
      }
    }

    // If no safe position found, go to opposite corner
    if (targetX === undefined || targetY === undefined) {
      targetX = cursorX < screenWidth / 2 ? screenWidth - 100 : 100;
      targetY = cursorY < screenHeight / 2 ? screenHeight - 100 : 100;
    }

    // Move time to the calculated position (relative to header)
    setTimeOffset({
      x: targetX - window.innerWidth / 2, // Center relative to screen
      y: targetY - 64, // Account for header height
    });

    // Reset back to original position after 3 seconds
    setTimeout(() => {
      setTimeOffset({ x: 0, y: 0 });
    }, 3000);
  };

  // Focus calculator when opened
  useEffect(() => {
    if (calcOpen && calcRef.current) {
      calcRef.current.focus();
    }
  }, [calcOpen]);

  // Load saved preferences on mount (keep single source of truth in authStore, but sync local prefs)
  useEffect(() => {
    try {
      const prefs = JSON.parse(localStorage.getItem("hria-prefs") || "{}");
      if (prefs.theme) updateTheme(prefs.theme);
      if (prefs.calcPosition) setCalcPosition(prefs.calcPosition);
      if (prefs.calcSize) setCalcSize(prefs.calcSize);
      if (prefs.expandedMenus) setExpandedMenus(prefs.expandedMenus);
    } catch {}
  }, [updateTheme]);

  // Save expanded menus state to localStorage
  useEffect(() => {
    try {
      const prefs = JSON.parse(localStorage.getItem("hria-prefs") || "{}");
      prefs.expandedMenus = expandedMenus;
      localStorage.setItem("hria-prefs", JSON.stringify(prefs));
    } catch {}
  }, [expandedMenus]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Apply theme to document with proper system detection
  const applyThemeToDocument = (t: Theme) => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");

    if (t === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      root.classList.add(systemTheme);
      root.setAttribute("data-theme", systemTheme);
    } else {
      root.classList.add(t);
      root.setAttribute("data-theme", t);
    }

    // Also set on body for better compatibility
    document.body.classList.remove("light", "dark");
    document.body.classList.add(
      t === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : t,
    );
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (theme === "system") {
        applyThemeToDocument("system");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  const toggleTheme = () => {
    const themes: Theme[] = ["light", "dark", "system"];
    const currentIndex = themes.indexOf(theme);
    const nextTheme = themes[(currentIndex + 1) % themes.length];
    updateTheme(nextTheme);

    // Save preference
    try {
      const prefs = JSON.parse(localStorage.getItem("hria-prefs") || "{}");
      prefs.theme = nextTheme;
      localStorage.setItem("hria-prefs", JSON.stringify(prefs));
    } catch {}
  };

  // Fetch current user's features when user changes
  useEffect(() => {
    if (user?.email) {
      fetchCurrentUserFeatures();
    }
  }, [user?.email, fetchCurrentUserFeatures]);

  // Ensure sidebar visible on large screens and persist state
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      }
    }
  }, []);

  // Don't close sidebar on large screens when navigating
  const handleNavigation = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  // Check if user has access to a specific feature
  const hasFeatureAccess = (feature?: string) => {
    if (!feature) return true;
    if (user?.role === "SuperAdmin") return true;
    if (
      currentFeatures?.features[
        feature as keyof typeof currentFeatures.features
      ]
    ) {
      return true;
    }
    // "manage" access is a superset of "view" access for Accounts.
    if (feature === "viewAccounts" && currentFeatures?.features.manageAccounts) {
      return true;
    }
    return false;
  };

  // Filter navigation items based on user role and feature access
  const filterNavItems = (items: any[]) => {
    return items.filter((item) => {
      const hasRoleAccess =
        !!user?.role && item.roles.includes(user.role as any);
      const featureOk = hasFeatureAccess(item.feature);
      return hasRoleAccess && featureOk;
    });
  };

  // Check if a submenu should be shown (has at least one accessible item)
  const shouldShowSubmenu = (submenuKey: string) => {
    const submenu =
      navigationStructure.submenus[
        submenuKey as keyof typeof navigationStructure.submenus
      ];
    const accessibleItems = filterNavItems(submenu.items);
    return accessibleItems.length > 0;
  };

  // Toggle submenu expansion - only one can be open at a time
  const toggleSubmenu = (submenuKey: string) => {
    setExpandedMenus((prev) => {
      const isCurrentlyExpanded = prev[submenuKey];
      // Close all menus first
      const newState = {
        donations: false,
        expenses: false,
        income: false,
        organization: false,
      };
      // If the clicked menu wasn't expanded, expand it
      if (!isCurrentlyExpanded) {
        newState[submenuKey as keyof typeof newState] = true;
      }
      return newState;
    });
  };

  // Get filtered navigation sections
  const filteredTopLevel = filterNavItems(navigationStructure.topLevel);
  const filteredBottomLevel = filterNavItems(navigationStructure.bottomLevel);

  // Detect printers (Electron) to gate donation creation
  useEffect(() => {
    (async () => {
      try {
        // @ts-ignore
        const list = await (window as any).electronAPI?.getPrinters?.();
        const hasPrintersDetected = Array.isArray(list) && list.length > 0;
        setHasPrinters(hasPrintersDetected);
      } catch {
        setHasPrinters(true); // in web, allow
      }
    })();
  }, []);

  // Handle menu actions from Electron
  React.useEffect(() => {
    // @ts-ignore - defined in preload.d.ts
    if (!(window as any)?.electronAPI?.onMenuAction) return;
    const handler = (action: string, ...args: any[]) => {
      if (action === "new-donation") {
        navigate("/add-donation");
      } else if (action === "new-donor") {
        if (hasFeatureAccess("manageDonors")) {
          navigate("/add-donors");
        }
      } else if (action === "new-guest-donor") {
        if (hasFeatureAccess("manageDonors")) {
          navigate("/add-guest-donors");
        }
      } else if (action === "new-expense") {
        if (hasFeatureAccess("addExpense")) {
          navigate("/add-expense");
        }
      } else if (action === "print-receipt") {
        navigate("/donations");
      } else if (action === "navigate" && args[0]) {
        navigate(args[0]);
      } else if (action === "find") {
        // Focus search if available, or show find dialog
        const searchInput = document.querySelector(
          'input[type="search"], input[placeholder*="Search"]',
        ) as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
    };
    // @ts-ignore
    (window as any).electronAPI.onMenuAction(handler);
    return () => {
      // @ts-ignore
      (window as any).electronAPI.removeAllListeners("menu-action");
    };
  }, [navigate, hasFeatureAccess]);

  // Calculator drag handlers
  const handleMouseDown = (e: React.MouseEvent, type: "drag" | "resize") => {
    e.preventDefault();
    if (type === "drag") {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - calcPosition.x,
        y: e.clientY - calcPosition.y,
      });
    } else {
      setIsResizing(true);
      setResizeStart({
        x: e.clientX - calcSize.width,
        y: e.clientY - calcSize.height,
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setCalcPosition({ x: newX, y: newY });
    } else if (isResizing) {
      const newWidth = e.clientX - resizeStart.x;
      const newHeight = e.clientY - resizeStart.y;
      setCalcSize({
        width: Math.max(280, newWidth),
        height: Math.max(320, newHeight),
      });
    }
  };

  const handleMouseUp = () => {
    if (isDragging || isResizing) {
      setIsDragging(false);
      setIsResizing(false);
      // Save position and size
      const prefs = JSON.parse(localStorage.getItem("hria-prefs") || "{}");
      prefs.calcPosition = calcPosition;
      prefs.calcSize = calcSize;
      localStorage.setItem("hria-prefs", JSON.stringify(prefs));
    }
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart]);

  const getThemeIcon = () => {
    switch (theme) {
      case "light":
        return <Sun className="h-5 w-5" />;
      case "dark":
        return <Moon className="h-5 w-5" />;
      default:
        return <Monitor className="h-5 w-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={`sidebar ${sidebarOpen ? "sidebar-visible" : "sidebar-hidden"}`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <img
                src="./hizb-ur-rahman-logo.png"
                alt="HRIA Logo"
                className="h-6 w-6 object-contain"
                onError={(e) => {
                  // Fallback to text if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  target.nextElementSibling?.classList.remove("hidden");
                }}
              />
              <span className="text-white font-bold text-lg hidden">H</span>
            </div>
            <span className="ml-3 text-xl font-bold text-gray-900">HRIA</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto pb-24 relative z-10 custom-scrollbar max-h-[calc(100vh-10rem)]">
          <nav className="mt-6 px-3">
            <div className="space-y-1">
              {/* Top Level Navigation */}
              {filteredTopLevel.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "nav-item-active" : ""}`
                  }
                  onClick={handleNavigation}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </NavLink>
              ))}

              {/* Donations Submenu */}
              {shouldShowSubmenu("donations") && (
                <div className="space-y-1">
                  <button
                    onClick={() => toggleSubmenu("donations")}
                    className="nav-item w-full text-left flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <CreditCard className="mr-3 h-5 w-5" />
                      Donations
                    </div>
                    {expandedMenus.donations ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {expandedMenus.donations && (
                    <div className="ml-6 space-y-1">
                      {filterNavItems(
                        navigationStructure.submenus.donations.items,
                      ).map((item) => (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          className={({ isActive }) =>
                            `nav-item text-sm ${isActive ? "nav-item-active" : ""}`
                          }
                          onClick={handleNavigation}
                        >
                          <item.icon className="mr-3 h-4 w-4" />
                          {item.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Expenses Submenu */}
              {shouldShowSubmenu("expenses") && (
                <div className="space-y-1">
                  <button
                    onClick={() => toggleSubmenu("expenses")}
                    className="nav-item w-full text-left flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <Receipt className="mr-3 h-5 w-5" />
                      Expenses
                    </div>
                    {expandedMenus.expenses ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {expandedMenus.expenses && (
                    <div className="ml-6 space-y-1">
                      {filterNavItems(
                        navigationStructure.submenus.expenses.items,
                      ).map((item) => (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          className={({ isActive }) =>
                            `nav-item text-sm ${isActive ? "nav-item-active" : ""}`
                          }
                          onClick={handleNavigation}
                        >
                          <item.icon className="mr-3 h-4 w-4" />
                          {item.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Income Submenu */}
              {shouldShowSubmenu("income") && (
                <div className="space-y-1">
                  <button
                    onClick={() => toggleSubmenu("income")}
                    className="nav-item w-full text-left flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <TrendingUp className="mr-3 h-5 w-5" />
                      Income
                    </div>
                    {expandedMenus.income ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {expandedMenus.income && (
                    <div className="ml-6 space-y-1">
                      {filterNavItems(
                        navigationStructure.submenus.income.items,
                      ).map((item) => (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          className={({ isActive }) =>
                            `nav-item text-sm ${isActive ? "nav-item-active" : ""}`
                          }
                          onClick={handleNavigation}
                        >
                          <item.icon className="mr-3 h-4 w-4" />
                          {item.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Organization Submenu */}
              {shouldShowSubmenu("organization") && (
                <div className="space-y-1">
                  <button
                    onClick={() => toggleSubmenu("organization")}
                    className="nav-item w-full text-left flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <Building2 className="mr-3 h-5 w-5" />
                      Organization
                    </div>
                    {expandedMenus.organization ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {expandedMenus.organization && (
                    <div className="ml-6 space-y-1">
                      {filterNavItems(
                        navigationStructure.submenus.organization.items,
                      ).map((item) => (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          className={({ isActive }) =>
                            `nav-item text-sm ${isActive ? "nav-item-active" : ""}`
                          }
                          onClick={handleNavigation}
                        >
                          <item.icon className="mr-3 h-4 w-4" />
                          {item.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Bottom Level Navigation */}
              {filteredBottomLevel.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "nav-item-active" : ""}`
                  }
                  onClick={handleNavigation}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </NavLink>
              ))}
            </div>
          </nav>
        </div>

        {/* User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white dark:bg-gray-900 border-t z-20">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 font-semibold">
                {user?.name?.charAt(0) || "U"}
              </span>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                {user?.name || "User"}
              </p>
              <p className="text-xs text-gray-500">{user?.role || "Role"}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
              title="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Header - Fixed */}
        <header className="fixed top-0 left-0 lg:left-64 z-40 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 w-full lg:w-[calc(100%-16rem)] h-16">
          <div className="flex items-center justify-between h-full px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-gray-700"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="ml-2 lg:ml-0 text-xl font-semibold text-gray-900 dark:text-white">
                {(() => {
                  // Find the current page name from navigation structure
                  const allNavItems = [
                    ...navigationStructure.topLevel,
                    ...navigationStructure.submenus.donations.items,
                    ...navigationStructure.submenus.expenses.items,
                    ...navigationStructure.submenus.income.items,
                    ...navigationStructure.submenus.organization.items,
                    ...navigationStructure.bottomLevel,
                  ];
                  return (
                    allNavItems.find(
                      (item: any) => item.href === location.pathname,
                    )?.name || "Dashboard"
                  );
                })()}
              </h1>
            </div>

            <div className="flex items-center space-x-3 pr-2">
              {/* Current Time with Fun Animation */}
              <div
                className="time-display p-2 text-sm text-gray-400 dark:text-gray-300 font-mono cursor-pointer select-none relative rounded-md hover:text-gray-600 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center"
                onMouseEnter={handleTimeHover}
                style={{
                  transform:
                    timeOffset.x !== 0 || timeOffset.y !== 0
                      ? `translate(${timeOffset.x}px, ${timeOffset.y}px)`
                      : "none",
                  transition: "all 0.3s ease-out",
                }}
              >
                <span className="inline-block animate-digital-rain leading-none">
                  {currentTime.toLocaleTimeString()}
                </span>
              </div>

              {/* Add Donation Button */}
              <button
                onClick={() => navigate("/add-donation")}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-gray-700 rounded-md"
                title="Add Donation"
              >
                <Plus className="h-5 w-5" />
              </button>

              {/* Calculator Button */}
              <button
                onClick={() => setCalcOpen((v) => !v)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-gray-700 rounded-md"
                title="Calculator"
              >
                <Calculator className="h-5 w-5" />
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-gray-700 rounded-md"
                title={`Theme: ${theme}`}
              >
                {getThemeIcon()}
              </button>

              {/* Refresh Button */}
              <button
                onClick={() => window.location.reload()}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-gray-700 rounded-md"
                title="Refresh App"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>

              {/* User Menu */}
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.name || "User"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.email || "user@example.com"}
                  </p>
                </div>
                <div className="h-8 w-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 font-semibold text-sm">
                    {user?.name?.charAt(0) || "U"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="pt-24 pb-6">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>

        {/* Status Bar */}
        <div className="fixed bottom-0 left-0 lg:left-64 right-0 h-8 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between px-4 text-xs z-30">
          <div className="flex items-center gap-4">
            <NetworkStatus />
          </div>
          <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
            <span>{APP_SHORT_NAME}</span>
            <span className="hidden sm:inline text-gray-300 dark:text-gray-600">·</span>
            <span className="hidden sm:inline">{APP_TAGLINE}</span>
            <span className="hidden sm:inline text-gray-300 dark:text-gray-600">|</span>
            <PraivoxBranding className="hidden sm:inline" />
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Floating Calculator */}
      {calcOpen && (
        <div
          ref={calcRef}
          className="fixed z-40 bg-white dark:bg-gray-800 rounded-lg shadow-soft border border-gray-200 dark:border-gray-700"
          style={{
            left: calcPosition.x,
            top: calcPosition.y,
            width: calcSize.width,
            height: calcSize.height,
          }}
          tabIndex={0}
          onKeyDown={(e) => {
            // Handle keyboard input for calculator
            if ((e.key >= "0" && e.key <= "9") || e.key === ".") {
              e.preventDefault();
              // Focus the calculator component
              const calcInput = calcRef.current?.querySelector("input");
              if (calcInput) {
                calcInput.focus();
                calcInput.value += e.key;
              }
            }
          }}
        >
          {/* Calculator Header - Draggable */}
          <div
            className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 cursor-move bg-gray-50 dark:bg-gray-700"
            onMouseDown={(e) => handleMouseDown(e, "drag")}
          >
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Calculator
            </span>
            <button
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={() => setCalcOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Calculator Content */}
          <div className="p-3 select-none">
            <div className="grid grid-cols-4 gap-2">
              <input
                className="col-span-4 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-right text-xl px-3 py-2 text-gray-900 dark:text-gray-100 shadow-sm"
                placeholder="0"
                defaultValue=""
                onKeyDown={(e) => {
                  const input = e.currentTarget;
                  if (e.key === "Enter") {
                    e.preventDefault();
                    try {
                      // eslint-disable-next-line no-new-func
                      const result = Function(
                        `return (${input.value || "0"})`,
                      )();
                      input.value = String(result);
                    } catch {}
                  }
                }}
                onFocus={(e) => e.currentTarget.select()}
              />
              {[
                "7",
                "8",
                "9",
                "/",
                "4",
                "5",
                "6",
                "*",
                "1",
                "2",
                "3",
                "-",
                "0",
                ".",
                "=",
                "+",
                "C",
                "⌫",
              ].map((key) => (
                <button
                  key={key}
                  className={`
                    ${key === "=" ? "col-span-2 bg-primary-600 hover:bg-primary-700 text-white" : ""}
                    ${key === "C" ? "bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/40 dark:hover:bg-red-900/60 dark:text-red-200" : ""}
                    ${key === "⌫" ? "bg-orange-100 hover:bg-orange-200 text-orange-700 dark:bg-orange-900/40 dark:hover:bg-orange-900/60 dark:text-orange-200" : ""}
                    ${!["=", "C", "⌫"].includes(key) ? "bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100" : ""}
                    rounded-md px-3 py-2 text-lg font-medium shadow-sm transition-colors
                  `}
                  onClick={() => {
                    const input = calcRef.current?.querySelector(
                      "input",
                    ) as HTMLInputElement | null;
                    if (!input) return;
                    if (key === "=") {
                      try {
                        // eslint-disable-next-line no-new-func
                        const result = Function(
                          `return (${input.value || "0"})`,
                        )();
                        input.value = String(result);
                      } catch {}
                      return;
                    }
                    if (key === "C") {
                      input.value = "";
                      return;
                    }
                    if (key === "⌫") {
                      input.value = input.value.slice(0, -1);
                      return;
                    }
                    input.focus();
                    input.value += key;
                  }}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
